import { useRef, useState, useEffect, useCallback } from 'react';
import { useUsingVagoSettings } from '../../../../../../hooks';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import EmailIcon from '@mui/icons-material/Email';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import { Quote } from '../../../../../../Typescript/backend/classes';
import { InsurancePolicy } from '../../../../../../Typescript/classes';
import { VagoDownloadApplicationdocumentationInput } from '../../../../../../gql/graphql';
import { Form, FormApi } from 'informed';
import { TextField, EmailInput, TextArea } from '@calefy-inc/informedMaterial';
import {
  WithFormStyles,
  withFormStyles,
} from '../../../../../../util/withFormStyles';
import { graphql } from '../../../../../../gql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { ErrorP } from '../../../../../common/Typography';
import { useAuth } from '@calefy-inc/authentication';
import { SuccessP } from '../../../../../common/Typography/components';
import {
  validateVagoEmailAddress,
  validateRequiredEmailList,
  validateRequired,
  generateSendToBrokerEmailBody,
  generateEmailSubject,
} from './utility/sendToBrokerUtilities';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import { Container } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import Card from '@mui/material/Card';
import { isNotNullish } from '../../../../../../util';
import { styled } from '@mui/material';
import { SupportLink } from '../../../../../common/SupportLink';
import Bugsnag from '@bugsnag/browser';
import { formatAsReadableList } from '../../../../../common/QuoteComponents/utility';

interface VagoSendToBrokerButtonProps {
  insurancePolicies: Array<InsurancePolicy>;
  quote: Quote;
}
export const VagoSendToBrokerButton = ({
  quote,
  insurancePolicies,
}: VagoSendToBrokerButtonProps) => {
  const onVago = useUsingVagoSettings();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  if (!onVago || insurancePolicies.length === 0) {
    return null;
  }
  return (
    <>
      <Button
        startIcon={<EmailIcon />}
        variant='contained'
        onClick={() => setDialogOpen(true)}
      >
        Send to broker
      </Button>
      <VagoSendToBrokerDialog
        quote={quote}
        insurancePolicies={insurancePolicies}
        open={dialogOpen}
        setOpen={(open: boolean) => setDialogOpen(open)}
      />
    </>
  );
};

interface VagoSendToBrokerDialogProps extends WithFormStyles {
  open: boolean;
  setOpen: (open: boolean) => void;
  quote: Quote;
  insurancePolicies: Array<InsurancePolicy>;
}
const VagoSendToBrokerDialog = withFormStyles()(
  ({
    quote,
    insurancePolicies,
    open,
    setOpen,
    classes,
  }: VagoSendToBrokerDialogProps) => {
    const formApiRef = useRef<FormApi>();
    const { token } = useAuth();

    const [files, setFiles] = useState<
      Array<VagoDownloadApplicationdocumentationInput>
    >([]);
    const [excluded, setExcluded] = useState<Array<string>>([]); // list of excluded filenames
    const [body, setBody] = useState<string>('');
    const [formErrors, setFormErrors] = useState<object>();
    const [vagoSendToBrokerEmail, { loading, error, data, reset }] =
      useMutation(VAGO_SEND_TO_BROKER_EMAIL);
    const [getVagoDestinationEmails, { data: destinationEmailsData }] =
      useLazyQuery(VAGO_GET_DESTINATION_EMAIL);
    const [getVagoBrokerEmailFiles, { data: fileData }] = useLazyQuery(
      VAGO_GET_BROKER_EMAIL_FILES,
    );

    // get the files once the token is here and the modal is opened
    useEffect(() => {
      if (token && open) {
        getVagoBrokerEmailFiles({
          variables: {
            token,
            quoteUuid: quote.uniqueId,
          },
        });
      }
    }, [token, open]);

    useEffect(() => {
      if (fileData?.vagoGetBrokerEmailFiles) {
        setFiles(
          // @ts-expect-error
          fileData.vagoGetBrokerEmailFiles
            .filter(isNotNullish)
            .map((backend) => {
              const { __typename, ...rest } = backend;
              return rest;
            }),
        );
      }
    }, [fileData]);

    // get the destination email
    useEffect(() => {
      if (token && quote && open) {
        getVagoDestinationEmails({
          variables: {
            token,
            quoteUuid: quote.uniqueId,
          },
        });
      }
    }, [token, quote, open]);

    // set the value in state once we get the destination email back
    useEffect(() => {
      if (destinationEmailsData && formApiRef.current) {
        const destinationEmail =
          destinationEmailsData.vagoGetDestinationEmails &&
            destinationEmailsData.vagoGetDestinationEmails.length > 0
            ? destinationEmailsData.vagoGetDestinationEmails[0]
            : null;
        if (destinationEmail) {
          formApiRef.current.setValue('fromEmail', destinationEmail);
        }
      }
    }, [destinationEmailsData]);

    // set the subject once we can
    useEffect(() => {
      if (formApiRef.current) {
        formApiRef.current.setValue('subject', generateEmailSubject(quote));
      }
    }, [quote]);

    // generate the body whenever the policies change
    useEffect(() => {
      if (insurancePolicies.length > 0) {
        setBody(generateSendToBrokerEmailBody(quote, insurancePolicies));
      }
    }, [insurancePolicies]);

    // whenever the body changes, overwrite the values in form state
    useEffect(() => {
      if (formApiRef.current) {
        formApiRef.current.setValue('body', body);
      }
    }, [body]);

    /**
     * Ensure that a filename is included in the list of excluded files
     */
    const excludeFile = useCallback(
      (filename: string) => {
        setExcluded((oldExcluded) => {
          return Array.from(new Set([...oldExcluded, filename]));
        });
      },
      [setExcluded],
    );

    /**
     * Ensure that a filename is not in the list of excluded files
     */
    const includeFile = useCallback(
      (filename: string) => {
        setExcluded((oldExcluded) => {
          return [...oldExcluded.filter((old) => old !== filename)];
        });
      },
      [setExcluded],
    );

    return (
      <Dialog
        open={open}
        PaperProps={{
          sx: { width: '85vw', minWidth: '450px', maxWidth: '2000px' },
        }}
      >
        <DialogTitle>Send to Broker</DialogTitle>
        <DialogContent sx={{ width: '85vw' }}>
          <Form
            className={classes.form}
            formApiRef={formApiRef}
            initialValues={{
              fromEmail:
                destinationEmailsData &&
                  destinationEmailsData.vagoGetDestinationEmails &&
                  destinationEmailsData.vagoGetDestinationEmails?.length > 0
                  ? destinationEmailsData.vagoGetDestinationEmails[0]
                  : undefined,
              subject: generateEmailSubject(quote),
              to: undefined,
              body,
            }}
            onSubmit={(formState) => {
              const { values } = formState;
              const to = String(values.to);
              const subject = String(values.subject);
              const body = String(values.body);
              const fromEmail = String(values.fromEmail);
              const filesToSend = files
                .filter((file) => !excluded.includes(file.filename))
                // @ts-expect-error
                .map(({ __typename, ...rest }) => rest); // Remove __typename

              // validation for this is handled in validateFields
              const actualToEmails = to
                .split(',')
                .map((email) => email.trim());

              vagoSendToBrokerEmail({
                variables: {
                  token,
                  fromEmail,
                  to: actualToEmails,
                  subject,
                  body,
                  files: filesToSend,
                },
              });
              setFormErrors(undefined);
            }
            }
            validateFields={(values) => {
              const fromEmail = values.fromEmail ? String(values.fromEmail) : '';
              const subject = values.subject ? String(values.subject) : '';
              const to = values.to ? String(values.to) : '';
              const body = values.body ? String(values.body) : '';
              return {
                fromEmail:
                  fromEmail
                    ? validateVagoEmailAddress(fromEmail)
                    : validateRequired(fromEmail),
                subject: validateRequired(subject),
                to: validateRequiredEmailList(to),
                body: validateRequired(body),
              };
            }}
            onSubmitFailure={(errors) => {
              setFormErrors(errors);
            }}
          >
            <EmailInput
              name='fromEmail'
              label='From'
              required={true}
              className={classes.emailinput}
              disabled={!!data}
              validate={validateVagoEmailAddress}
            />
            <LittleP>
              This address will be included as a recipient to maintain an
              archived copy of the email.
            </LittleP>
            <div>
              <EmailInput
                name='to'
                label='To'
                required={true}
                className={classes.emailinput}
                disabled={!!data}
                sx={{ mt: 0 }}
              />
              <LittleP sx={{ mt: 0 }}>
                Please enter a comma-separated list of emails
              </LittleP>
            </div>
            <TextField
              name='subject'
              label='Subject'
              required={true}
              className={classes.textfield}
              disabled={!!data}
            />
            <TextArea
              name='body'
              label='Body'
              required={true}
              className={classes.textarea}
              disabled={!!data}
              minRows={10}
            />
            <VagoSendToBrokerFiles
              files={files}
              excluded={excluded}
              includeFile={includeFile}
              excludeFile={excludeFile}
            />
          </Form>
          <ErrorEtcContainer>
            {data?.vagoSendToBrokerEmail?.emails ? (
              <div>
                <SuccessP>Success!</SuccessP>
                <Typography>
                  The email has been sent to{' '}
                  {formatAsReadableList(
                    data.vagoSendToBrokerEmail?.emails.filter(isNotNullish),
                  )}
                  . They should receive it within the next few minutes.
                </Typography>
              </div>
            ) : null}
            {error ? <DisplayError error={error} /> : null}
            <DisplayFormErrors formErrors={formErrors} />
          </ErrorEtcContainer>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              reset();
            }}
          >
            {data?.vagoSendToBrokerEmail?.emails ? 'Done' : 'Cancel'}
          </Button>
          <LoadingButton
            disabled={!!data?.vagoSendToBrokerEmail?.emails}
            loading={loading}
            variant='contained'
            onClick={() => {
              if (formApiRef.current) {
                formApiRef.current.submitForm();
              }
            }}
          >
            Send to Broker
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);

const LittleP = styled(Typography)(({ theme }) => {
  return { fontSize: `calc(0.75 * ${theme.typography.body1.fontSize})` };
});

interface DisplayErrorProps {
  error: Error;
}
const DisplayError = ({ error }: DisplayErrorProps) => {
  useEffect(() => {
    console.error(error);
    Bugsnag.notify(error);
  }, [error]);

  return (
    <>
      <ErrorP>Unexpected Error</ErrorP>
      <Typography>
        We're sorry, but there's been an unexpected error when sending this
        email. Our support team has been notified. Please refresh the page and
        try again. If the error persists, please contact our suport team at{' '}
        <SupportLink subject='Error sending broker email' /> with any additional
        details.
      </Typography>
      <Typography>Our apologies for the inconvenience.</Typography>
    </>
  );
};

const ErrorEtcContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

interface DisplayFormErrorsProps {
  formErrors?: object;
}
const DisplayFormErrors = ({ formErrors }: DisplayFormErrorsProps) => {
  if (!formErrors || Object.values(formErrors).length === 0) {
    return null;
  }
  // @ts-expect-error
  const { fromEmail, subject, to, body } = formErrors;
  const expectedErrors = [fromEmail, subject, to, body].filter(isNotNullish);
  return (
    <FormErrorsContainer>
      <ErrorP>
        Error{expectedErrors.length > 1 ? 's' : ''} Sending Email:
      </ErrorP>
      <ul>
        {fromEmail ? <li>From Email: {fromEmail}</li> : null}
        {subject ? <li>Subject: {subject}</li> : null}
        {to ? <li>To: {to}</li> : null}
        {body ? <li>Body: {body}</li> : null}
      </ul>
    </FormErrorsContainer>
  );
};

const FormErrorsContainer = styled('div')({});

interface VagoSendToBrokerFilesProps {
  files: Array<VagoDownloadApplicationdocumentationInput>;
  excluded: Array<string>;
  excludeFile: (filename: string) => void;
  includeFile: (filename: string) => void;
}
const VagoSendToBrokerFiles = ({
  files,
  excluded,
  excludeFile,
  includeFile,
}: VagoSendToBrokerFilesProps) => {
  if (files.length === 0) {
    return null;
  }
  return (
    <DisplayFileContainer>
      <Typography component='h3' variant='h5'>
        Attachments
      </Typography>
      <StyledContainer>
        {files.map((file) => (
          <DisplayFile
            key={file.filename}
            file={file}
            isExcluded={excluded.includes(file.filename)}
            includeFile={() => includeFile(file.filename)}
            excludeFile={() => excludeFile(file.filename)}
          />
        ))}
      </StyledContainer>
    </DisplayFileContainer>
  );
};

const StyledContainer = styled(Container)(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  };
});

const DisplayFileContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
  };
});

interface DisplayFileProps {
  file: VagoDownloadApplicationdocumentationInput;
  isExcluded: boolean;
  includeFile: () => void;
  excludeFile: () => void;
}
const DisplayFile = ({
  file,
  isExcluded,
  includeFile,
  excludeFile,
}: DisplayFileProps) => {
  return (
    <FileContainer isExcluded={isExcluded}>
      <FileIcon filename={file.filename} />
      <Typography>
        <FileLink
          href={`data:application/octet-stream;base64,${file.data}`}
          download={file.filename}
          isExcluded={isExcluded}
        >
          {file.filename}
        </FileLink>
      </Typography>
      <Checkbox
        checked={!isExcluded}
        onChange={(e) => {
          if (!e.target.checked) {
            excludeFile();
          } else {
            includeFile();
          }
        }}
      />
    </FileContainer>
  );
};

interface ContainsIsExcludedProps {
  isExcluded?: boolean;
}
const FileLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'isExcluded',
})<ContainsIsExcludedProps>(({ isExcluded }) => {
  return {
    textDecoration: isExcluded ? 'line-through' : 'none',
    opacity: isExcluded ? 0.5 : 1,
    color: 'inherit',
  };
});

const FileContainer = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isExcluded',
})<ContainsIsExcludedProps>(({ theme, isExcluded }) => {
  return {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    boxShadow: isExcluded ? 'none' : undefined,
  };
});

interface FileIconProps {
  filename: string;
}
function FileIcon({ filename }: FileIconProps) {
  if (filename.match(/\.pdf$/i)) {
    return (
      <PictureAsPdfIcon color='error' sx={{ verticalAlign: 'middle', mr: 1 }} />
    );
  }
  if (filename.match(/\.xlsx?$/i)) {
    return (
      <TableChartIcon color='success' sx={{ verticalAlign: 'middle', mr: 1 }} />
    );
  }
  if (filename.match(/\.docx?$/i)) {
    return (
      <DescriptionIcon
        color='primary'
        sx={{ verticalAlign: 'middle', mr: 1 }}
      />
    );
  }
  // default
  return <InsertDriveFileIcon sx={{ verticalAlign: 'middle', mr: 1 }} />;
}

export const VAGO_GET_BROKER_EMAIL_FILES = graphql(`
  query vagoGetBrokerEmailFiles($token: String!, $quoteUuid: String!) {
    vagoGetBrokerEmailFiles(token: $token, quoteUuid: $quoteUuid) {
      filename
      data
    }
  }
`);

export const VAGO_SEND_TO_BROKER_EMAIL = graphql(`
  mutation vagoSendToBrokerEmail(
    $token: String!
    $fromEmail: String!
    $to: [String!]!
    $subject: String!
    $body: String!
    $files: [VagoDownloadApplicationdocumentationInput]!
  ) {
    vagoSendToBrokerEmail(
      token: $token
      fromEmail: $fromEmail
      to: $to
      subject: $subject
      body: $body
      files: $files
    ) {
      emails
    }
  }
`);

export const VAGO_GET_DESTINATION_EMAIL = graphql(`
  query vagoGetDestinationEmails($token: String!, $quoteUuid: ID!) {
    vagoGetDestinationEmails(token: $token, quoteUuid: $quoteUuid)
  }
`);
